/** Client-side background removal for logos / flat graphics (corner flood + optional interior). */

export type RemoveBgOptions = {
  /** Also clear matching pixels inside the drawing (not only from edges). */
  interior?: boolean;
  /** Color distance 0–441 (RGB euclidean). Default ~48. */
  tolerance?: number;
};

function colorDist(r: number, g: number, b: number, br: number, bg: number, bb: number): number {
  const dr = r - br;
  const dg = g - bg;
  const db = b - bb;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image_load_failed'));
    img.src = url;
  });
}

/**
 * Make near-background pixels transparent.
 * Works well for solid / near-uniform backgrounds (typical print logos).
 */
export async function removeImageBackground(
  imageUrl: string,
  options: RemoveBgOptions = {},
): Promise<Blob> {
  const tolerance = options.tolerance ?? 48;
  const interior = !!options.interior;
  const img = await loadImage(imageUrl);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('canvas_unavailable');
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  const sample = (x: number, y: number) => {
    const i = (y * w + x) * 4;
    return [data[i]!, data[i + 1]!, data[i + 2]!] as const;
  };
  const corners = [
    sample(0, 0),
    sample(w - 1, 0),
    sample(0, h - 1),
    sample(w - 1, h - 1),
  ];
  const br = Math.round(corners.reduce((s, c) => s + c[0], 0) / 4);
  const bg = Math.round(corners.reduce((s, c) => s + c[1], 0) / 4);
  const bb = Math.round(corners.reduce((s, c) => s + c[2], 0) / 4);

  const matches = (i: number) =>
    colorDist(data[i]!, data[i + 1]!, data[i + 2]!, br, bg, bb) <= tolerance;

  if (interior) {
    for (let i = 0; i < data.length; i += 4) {
      if (matches(i)) data[i + 3] = 0;
    }
  } else {
    // Flood from edges only
    const visited = new Uint8Array(w * h);
    const stack: number[] = [];
    const push = (x: number, y: number) => {
      if (x < 0 || y < 0 || x >= w || y >= h) return;
      const idx = y * w + x;
      if (visited[idx]) return;
      visited[idx] = 1;
      const i = idx * 4;
      if (!matches(i)) return;
      data[i + 3] = 0;
      stack.push(idx);
    };
    for (let x = 0; x < w; x++) {
      push(x, 0);
      push(x, h - 1);
    }
    for (let y = 0; y < h; y++) {
      push(0, y);
      push(w - 1, y);
    }
    while (stack.length) {
      const idx = stack.pop()!;
      const x = idx % w;
      const y = (idx / w) | 0;
      push(x + 1, y);
      push(x - 1, y);
      push(x, y + 1);
      push(x, y - 1);
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('toBlob_failed'));
    }, 'image/png');
  });
}
